import { useState } from 'react';
import { authenticatedRequest } from '../services/authApi';
import { buildLocalInterpretationsFromChart } from '../components/natal/natalInterpretationHelpers';
import { isEnglishPresentation, localizedValue } from '../localization/presentationLanguage';

export const NATAL_TEMPORAL_ERROR_MESSAGES = Object.freeze({
  invalid_birth_date: 'La fecha de nacimiento no es válida.',
  invalid_birth_time: 'La hora de nacimiento debe tener formato HH:mm y ser válida.',
  invalid_birth_timezone: 'La zona horaria del lugar de nacimiento no es válida.',
  birth_time_nonexistent: 'Esa hora local no existió por un cambio de horario. Revisá la hora de nacimiento.',
  birth_time_ambiguous: 'Esa hora ocurrió dos veces por un cambio de horario. Este formulario todavía no permite elegir entre ambas ocurrencias.',
  birth_offset_mismatch: 'La zona horaria seleccionada no coincide con esa fecha y hora.',
  birth_timezone_required: 'No pudimos confirmar automáticamente la zona horaria de este lugar. Intentá buscarlo nuevamente o elegí otra sugerencia.',
  birth_time_required: 'La hora de nacimiento es obligatoria para esta carta natal.',
  natal_temporal_data_incomplete: 'Completá los datos temporales de nacimiento.',
});

const NATAL_TEMPORAL_ERROR_MESSAGES_EN = Object.freeze({
  invalid_birth_date: 'The date of birth is invalid.', invalid_birth_time: 'The time of birth must use a valid HH:mm format.',
  invalid_birth_timezone: 'The birth location time zone is invalid.', birth_time_nonexistent: 'That local time did not exist because of a clock change. Check the time of birth.',
  birth_time_ambiguous: 'That time occurred twice because of a clock change. This form cannot yet choose between the two occurrences.',
  birth_offset_mismatch: 'The selected time zone does not match that date and time.', birth_timezone_required: 'We could not automatically confirm this place’s time zone. Search again or choose another suggestion.',
  birth_time_required: 'The time of birth is required for this natal chart.', natal_temporal_data_incomplete: 'Complete the birth date and time information.',
});

const temporalMessage = (code) => (isEnglishPresentation() ? NATAL_TEMPORAL_ERROR_MESSAGES_EN : NATAL_TEMPORAL_ERROR_MESSAGES)[code];

export function buildNatalProfileRequest(form, resolved = {}) {
  const birthTimezone = resolved.birthTimezone ?? form.birthTimezone;
  if (!birthTimezone) {
    const error = new Error(temporalMessage('birth_timezone_required'));
    error.code = 'birth_timezone_required';
    throw error;
  }
  const offset = resolved.birthTzOffsetMin ?? form.birthTzOffsetMin;
  return {
    birthDate: form.birthDate,
    birthTime: form.birthTime,
    birthPlace: resolved.birthPlace ?? form.birthPlace,
    birthLat: resolved.birthLat ?? form.birthLat,
    birthLon: resolved.birthLon ?? form.birthLon,
    birthTimezone,
    ...(typeof offset === 'number' ? { birthTzOffsetMin: offset } : {}),
    birthCountry: form.birthCountry,
    birthProvince: form.birthProvince,
  };
}

export function natalErrorMessage(error) {
  const code = error?.payload?.error || error?.data?.error || error?.code;
  return temporalMessage(code) || error?.message || localizedValue({ es: '✨ No se pudo calcular tu carta. Intentá nuevamente.', en: '✨ Your chart could not be calculated. Please try again.' });
}

function toSearchParams(values) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null) params.set(key, String(value));
  });
  return params;
}

export default function useNatalChart({
  form,
  authUserId,
  resolveTypedPlaceData,
  applyResolvedTypedPlace,
}) {
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [chart, setChart] = useState(null);
  const [summary, setSummary] = useState(null);
  const [interpretations, setInterpretations] =
    useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const saveProfileOrThrow = async () => {
    let latestPlace = String(
      form.birthPlace || ''
    ).trim();

    let latestLat = form.birthLat;
    let latestLon = form.birthLon;
    let latestTzOffsetMin = form.birthTzOffsetMin;
    let latestTimezone = form.birthTimezone;

    if (latestLat == null || latestLon == null) {
      const typed = String(
        form.birthCityQuery || ''
      ).trim();

      if (typed.length >= 3) {
        const resolved =
          await resolveTypedPlaceData(typed);

        if (resolved) {
          latestPlace = String(
            resolved.birthPlace || ''
          ).trim();

          latestLat = resolved.birthLat;
          latestLon = resolved.birthLon;
          latestTzOffsetMin =
            resolved.birthTzOffsetMin;
          latestTimezone = resolved.birthTimezone;

          applyResolvedTypedPlace(resolved);
        }
      }
    }

    if (
      !form.birthDate ||
      !form.birthTime ||
      !latestPlace ||
      !latestTimezone
    ) {
      throw new Error(
        !latestTimezone
          ? temporalMessage('birth_timezone_required')
          : localizedValue({ es: 'Completá fecha, hora y lugar de nacimiento.', en: 'Complete your date, time, and place of birth.' })
      );
    }

    await authenticatedRequest(
      '/api/natal/profile',
      {
        method: 'POST',
        body: buildNatalProfileRequest(form, {
          birthPlace: latestPlace,
          birthLat: latestLat,
          birthLon: latestLon,
          birthTzOffsetMin: latestTzOffsetMin,
          birthTimezone: latestTimezone,
        }),
        credentials: 'include',
        diagSource: 'useNatalChart.saveProfile',
      }
    );
  };

  const saveAndFetchChart = async () => {
    setError('');
    setSuccess('');
    setLoadingSave(true);
    setLoadingChart(true);

    try {
      await saveProfileOrThrow();

      const params = toSearchParams({ userId: authUserId });
      const data = await authenticatedRequest(`/api/natal/chart?${params.toString()}`, {
        credentials: 'include',
        diagSource: 'useNatalChart.chart',
      });

      if (data?.error === 'rate_limited') {
        setError(
          localizedValue({ es: '🌌 Estamos calculando muchas cartas al mismo tiempo. Esperá unos minutos y volvé a intentarlo.', en: '🌌 Many charts are being calculated right now. Wait a few minutes and try again.' })
        );
        return;
      }

      if (data?.error === 'cooldown') {
        if (chart) {
          setSuccess(
            data?.message ||
              localizedValue({ es: '🌟 Tu carta ya está calculada. Podés verla todas las veces que quieras.', en: '🌟 Your chart is ready. You can view it whenever you like.' })
          );
        } else {
          setError(
            data?.message ||
              localizedValue({ es: '✨ Ya estamos calculando tu carta. Esperá unos segundos y volvé a intentar.', en: '✨ Your chart is already being calculated. Wait a few seconds and try again.' })
          );
        }

        return;
      }

      if (data?.ok && data.chart) {
        setChart(data.chart);

        const local =
          buildLocalInterpretationsFromChart(
            data.chart
          );

        setSummary(
          data.summary ||
            local.summary ||
            null
        );

        setInterpretations(
          (!isEnglishPresentation() && data.interpretations)
            ? data.interpretations
            : local.interpretations
              ? {
                  interpretationVersion:
                    'local-fallback-v1',
                  ...local.interpretations,
                }
              : null
        );

        setSuccess(
          data.cached
            ? localizedValue({ es: '🌟 Mostrando tu carta guardada (sin recalcular).', en: '🌟 Showing your saved chart without recalculating.' })
            : localizedValue({ es: '✨ Carta calculada y datos guardados correctamente.', en: '✨ Chart calculated and birth information saved.' })
        );
      } else {
        setError(
          data?.message ||
            localizedValue({ es: 'Completá tus datos natales para ver tu carta.', en: 'Complete your birth information to view your chart.' })
        );
      }
    } catch (requestError) {
      setError(
        natalErrorMessage(requestError)
      );
    } finally {
      setLoadingSave(false);
      setLoadingChart(false);
    }
  };

  return {
    chart,
    error,
    interpretations,
    loadingChart,
    loadingSave,
    saveAndFetchChart,
    success,
    summary,
  };
}
